/**
 * Plan-tier vocabulary + points formatting (Settings, Salary Selection,
 * Change Salary). The admin Users tab that this module was first written for
 * was deleted (#470, 2026-10-16), and its `energyCell` helper with it.
 *
 * Pure functions, deliberately outside DashboardPage.jsx so they can be unit
 * tested without mounting a page that pulls in the router and every service.
 *
 * TWO RULES LIVE HERE AND MUST NOT DRIFT:
 *
 *  1. **The canonical tier vocabulary is product wording, not config.** The
 *     paid tiers are named as SALARIES, exactly as the marketing site names
 *     them (Salary page #435): Entry Salary / Associate Salary / Partner Salary
 *     (es: Sueldo de entrada / de asociado / de socio — the i18n `plan_*`
 *     keys). Only the WORDS changed: the stored ids stay base / executive /
 *     enterprise, and the legacy normal/premium/max spellings remain valid
 *     ALIASES that prod rows still carry. Resolving an alias is the BACKEND's job — it ships
 *     `plan_canonical` precisely so this module never re-implements
 *     PLAN_ALIASES and the two can never disagree.
 *
 *  2. **Points are rendered VERBATIM.** The backend sends 2dp strings because
 *     they are money-like quantities reconciled against a statement.
 *     Passing one through parseFloat/toFixed would hand the rounding contract
 *     to the client — so nothing here ever converts a points string to a
 *     Number, not even to add a thousands separator.
 */

/**
 * Every tier that exists, in ascending order. Values are what the API stores.
 *
 * ⚠️ No longer "the tiers an admin may assign" — the operator plan endpoint was
 * deleted (2026-10-01), so nothing assigns a tier from a list any more. This is
 * now the vocabulary for LABELLING one (`PLAN_LABELS`) and the superset the self-serve checkout list is derived from.
 */
export const PLAN_OPTIONS = [
    { value: 'free', label: 'Free' },
    { value: 'base', label: 'Entry Salary' },
    { value: 'executive', label: 'Associate Salary' },
    { value: 'enterprise', label: 'Partner Salary' },
];

/**
 * The tiers a USER may pick for themselves during onboarding (#402).
 *
 * Free is deliberately absent, and this is a PRODUCT rule rather than a filter of
 * convenience: since the Salary Gate clears on a confirmed payment, offering a
 * tier that cannot be paid for would be offering a way past the paywall. Free
 * remains a real, storable plan — nothing in the product grants it since the
 * operator plan endpoint was deleted (2026-10-01) — so it stays in
 * `PLAN_OPTIONS` (which still has to LABEL such a row) and is subtracted here.
 *
 * Mirrors BE `salary_selection.SELF_SERVE_PLANS`; derived from PLAN_OPTIONS so a
 * new tier appears in both lists at once.
 */
export const SELF_SERVE_PLAN_OPTIONS = PLAN_OPTIONS.filter((o) => o.value !== 'free');

const PLAN_LABELS = PLAN_OPTIONS.reduce((acc, o) => ({ ...acc, [o.value]: o.label }), {});

/**
 * The tier a row's dropdown/chip should show.
 *
 * Prefers the backend's `plan_canonical`; falls back to `plan` (which this
 * endpoint already normalizes) so an older payload still renders. An
 * unrecognised value is passed through rather than swallowed — an admin seeing
 * a strange tier name is the point at which someone investigates.
 */
export function planValue(user) {
    return user?.plan_canonical || user?.plan || '';
}

/** Human label for a tier value; unknown values render as themselves. */
export function planLabel(value) {
    if (!value) return '';
    return PLAN_LABELS[value] || value;
}

/**
 * A 2dp points STRING as an integer number of hundredths, or null.
 *
 * The whole reason the wire format is a string is that these are money-like
 * quantities whose rounding belongs to the server. Parsing one with parseFloat
 * to do arithmetic hands that contract straight back to IEEE-754 — so the
 * arithmetic is done in BigInt hundredths instead, which is exact by
 * construction. Anything that is not a plain 2dp decimal is rejected rather
 * than coerced: a silently-zeroed figure is worse than a missing one.
 */
function ptsToCents(value) {
    if (value == null) return null;
    const s = String(value).trim();
    if (!/^-?\d+(\.\d{1,2})?$/.test(s)) return null;
    const negative = s.startsWith('-');
    const [whole, frac = ''] = s.replace('-', '').split('.');
    const cents = BigInt(whole) * 100n + BigInt(`${frac}00`.slice(0, 2));
    return negative ? -cents : cents;
}

/**
 * "Percentage of energy points consumed" this cycle, to EXACTLY two decimals
 * (Salary for subscription #312) — e.g. `"62.25"`.
 *
 *     (opening − remaining) ÷ opening × 100
 *
 * `opening` is the cycle's snapshot of the spendable tank, which is the only
 * honest denominator: unspent points roll over, so a plan's deposit alone does
 * not describe what this cycle actually started with.
 *
 * Returns `"0.00"` when there is nothing to divide by — a free account, or a
 * tank that has not been settled yet. Reporting "100%" or NaN for a user who
 * never had a tank would be a fabricated claim about their account.
 *
 * The low end is clamped at zero (a mid-cycle top-up can push the remaining
 * balance ABOVE what the cycle opened with, and "−8% consumed" is nonsense).
 * The HIGH end deliberately is NOT clamped: while enforcement is off a user can
 * genuinely spend past their opening tank, and rounding that down to a tidy
 * 100% would hide exactly the overspend this figure exists to show.
 */
export function consumedPercent(energy) {
    const opening = ptsToCents(energy?.cycle_opening_action_pts);
    const remaining = ptsToCents(energy?.action_pts);
    if (opening == null || remaining == null || opening <= 0n) return '0.00';

    const used = opening - remaining;
    const spent = used < 0n ? 0n : used;

    // ×10000 = ×100 for the percentage, ×100 for the two decimals. The
    // half-opening addend is integer ROUND-HALF-UP, matching the backend's own
    // quantizing so the two never disagree about which way a half went.
    const scaled = (spent * 10000n + opening / 2n) / opening;
    return `${scaled / 100n}.${String(scaled % 100n).padStart(2, '0')}`;
}

/**
 * A salary's monthly points for DISPLAY — "20,000" (en) / "20.000" (es).
 *
 * Salary&points: Alignment (#436): the number on the Salary Selection page must
 * be the number the backend deposits, so the page never carries its own copy —
 * it renders `salary_selection.tiers[].deposit_pts` (a 2dp STRING from
 * BE `plans.PLAN_DEPOSIT_PTS`) through this. Rule 2 above still holds: the
 * string is regrouped as text and never round-tripped through a Number.
 * Thousands are ALWAYS grouped, as the marketing site does (es-ES Intl would
 * leave a 4-digit figure ungrouped). Returns null for anything that is not a
 * plain non-negative decimal, so a malformed payload renders nothing rather than
 * a wrong number.
 */
export function formatTierPoints(value, language = 'en') {
    const parts = splitDecimal(value);
    if (!parts) return null;
    const es = language === 'es';
    const grouped = parts.whole.replace(/\B(?=(\d{3})+(?!\d))/g, es ? '.' : ',');
    return parts.frac ? `${grouped}${es ? ',' : '.'}${parts.frac}` : grouped;
}

/**
 * A salary's VAT-exclusive monthly price — "€20" (en) / "20 €" (es), the same
 * shape the marketing site's Intl currency formatter produces. From
 * `salary_selection.tiers[].price_eur`; null when absent or malformed.
 */
export function formatTierPrice(value, language = 'en') {
    const amount = formatTierPoints(value, language);
    if (amount == null) return null;
    return language === 'es' ? `${amount}\u00a0€` : `€${amount}`;
}

function splitDecimal(value) {
    if (value == null) return null;
    const s = String(value).trim();
    if (!/^\d+(\.\d{1,2})?$/.test(s)) return null;
    const [whole, frac = ''] = s.split('.');
    // "20000.00" → "20000"; "19.5" → "19.50" (money keeps two places or none).
    const f = frac.replace(/0+$/, '');
    return { whole: whole.replace(/^0+(?=\d)/, ''), frac: f ? f.padEnd(2, '0') : '' };
}
